import axios, { CancelTokenSource } from 'axios';
import { useCallback, useEffect, useRef, useState } from 'react';

import { DiallerGroup, UpdateDiallerGroup } from '~pages/CampaignManagement/domain';
import { useAppConfiguration } from '~providers/AppConfigurationProvider';

import { archiveDiallerGroupById, getDiallerGroup, triggerDiallerGroupReload, updateDiallerGroup } from './api';

const useDiallerGroup = (diallerGroupId: number, shouldFetch = true) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | undefined>(undefined);
  const [data, setData] = useState<DiallerGroup | undefined>(undefined);
  const axiosCancelRef = useRef<CancelTokenSource>(axios.CancelToken.source());
  const appConfig = useAppConfiguration();

  const reload = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    let resp: DiallerGroup | undefined;
    try {
      axiosCancelRef.current = axios.CancelToken.source();
      resp = await getDiallerGroup(diallerGroupId, axiosCancelRef.current);
    } catch (e) {
      setError((e as Error).message);
      setLoading(false);
      return;
    }

    // Returns undefined if request is canceled
    if (resp === undefined) return;

    setData(resp);
    setLoading(false);
  }, [diallerGroupId]);

  const update = useCallback(
    async (newData: Partial<UpdateDiallerGroup>) => {
      try {
        await updateDiallerGroup(diallerGroupId, newData);
      } catch (e) {
        // Do nothing
        return Promise.reject(e);
      }

      try {
        if (
          appConfig.extensions.predictive !== undefined &&
          ((newData.campaignType !== undefined && newData.campaignType != data!.campaignType) ||
            (newData.diallerType !== undefined && newData.diallerType != data!.diallerType))
        ) {
          await triggerDiallerGroupReload(appConfig.extensions.predictive.diallerURL);
        }
      } catch (e) {
        console.error('! Failed to trigger dialler group reload for predictive dialler.');
      }

      await reload();
    },
    [diallerGroupId],
  );

  const archive = useCallback(async () => {
    try {
      await archiveDiallerGroupById(diallerGroupId);
    } catch (e) {
      // Do nothing
      return Promise.reject(e);
    }

    await reload();
  }, [diallerGroupId]);

  useEffect(() => {
    setData(undefined);
  }, [diallerGroupId]);

  useEffect(() => {
    if (!shouldFetch) {
      return;
    }

    (async () => {
      setLoading(true);
      setError(undefined);

      let resp: DiallerGroup | undefined;
      try {
        axiosCancelRef.current = axios.CancelToken.source();
        resp = await getDiallerGroup(diallerGroupId, axiosCancelRef.current);
      } catch (e) {
        setError((e as Error).message);
        setLoading(false);
        return;
      }

      // Returns undefined if request is canceled
      if (resp === undefined) return;

      setData(resp);
      setLoading(false);
    })();

    return () => {
      // Cancel request if it has already been executed
      axiosCancelRef.current.cancel();
    };
  }, [diallerGroupId, shouldFetch]);

  return { loading, error, data, reload, update, archive };
};

export default useDiallerGroup;
